// src/pages/admin/ExpenseTracker.js
import React, { useEffect, useState, useCallback, useLayoutEffect, useRef } from "react";
import { Card, Row, Col, Button, Modal, Form, Table, Spinner } from "react-bootstrap";
import { addTransaction, getTransactions, getTransactionsSummary, deleteTransaction } from "../../service/apiService";

// amCharts imports
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

function ExpenseTracker({ user }) {
  // transactions list + summary
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  // add modal
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    tx_type: 'expense',
    amount: '',
    category: '',
    spent_from: 'company_account',
    spent_by: '',
    description: '',
    tx_date: new Date().toISOString().slice(0,16),
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // date range filter
  const [range, setRange] = useState('this_month'); // 'this_week' | 'this_month' | 'this_year'
  const [fromToParams, setFromToParams] = useState({ from: null, to: null }); // ISO strings

  // amcharts refs
  const chartDivRef = useRef(null);
  const chartRootRef = useRef(null);

  // helper: compute from/to based on range
  const computeRange = useCallback((r) => {
    const now = new Date();
    let from, to;
    if (r === 'this_week') {
      // start of ISO week (Monday)
      const day = now.getDay(); // 0 Sun..6 Sat
      const diff = (day === 0 ? 6 : day - 1); // days since Monday
      from = new Date(now);
      from.setDate(now.getDate() - diff);
      from.setHours(0,0,0,0);
      to = new Date(now); // up to now
      to.setHours(23,59,59,999);
    } else if (r === 'this_month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      to = new Date(now);
      to.setHours(23,59,59,999);
    } else if (r === 'this_year') {
      from = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
      to = new Date(now);
      to.setHours(23,59,59,999);
    } else {
      // fallback to month
      from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      to = new Date(now);
      to.setHours(23,59,59,999);
    }
    // return ISO strings compatible with PHP strtotime
    return { from: from.toISOString().slice(0,19).replace('T',' '), to: to.toISOString().slice(0,19).replace('T',' ') };
  }, []);

  // load summary (with range)
  const loadSummary = useCallback(async (params = {}) => {
    setLoadingSummary(true);
    try {
      const res = await getTransactionsSummary(params);
      if (res.status === 'success') setSummary(res);
    } catch (err) { console.error(err); }
    setLoadingSummary(false);
  }, []);

  // load transactions (with range + pagination)
  const loadTransactions = useCallback(async (p=1, l=50, params = {}) => {
    setLoadingTx(true);
    try {
      const allParams = { page: p, limit: l, ...params };
      const res = await getTransactions(allParams);
      if (res.status === 'success') {
        setTransactions(res.data);
        setTotal(res.total || 0);
        setPage(p);
        setLimit(l);
      } else {
        setTransactions([]);
        setTotal(0);
      }
    } catch (err) { console.error(err); setTransactions([]); setTotal(0); }
    setLoadingTx(false);
  }, []);

  // when range changes, compute from/to and reload data
  useEffect(() => {
    const ft = computeRange(range);
    setFromToParams(ft);
  }, [range, computeRange]);

  // reload whenever fromToParams change (or page/limit)
  useEffect(() => {
    if (!fromToParams.from) return;
    const params = { from: fromToParams.from, to: fromToParams.to };
    loadSummary(params);
    loadTransactions(1, limit, params);
  }, [fromToParams, loadSummary, loadTransactions, limit]);

  // initial compute
  useEffect(() => {
    setRange('this_month');
  }, []);

  // ------------------- amCharts render using summary -------------------
  useLayoutEffect(() => {
    if (!summary || !chartDivRef.current) return;
    if (chartRootRef.current) { try { chartRootRef.current.dispose(); } catch (e) {} chartRootRef.current = null; }

    const root = am5.Root.new(chartDivRef.current);
    chartRootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true, panY: false, wheelX: "panX", wheelY: "zoomX",
        pinchZoomX: true
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20 });
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      paddingTop: 10,
      maxWidth: 120,
      oversizedBehavior: "wrap"
    });

    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: "label", renderer: xRenderer, tooltip: am5.Tooltip.new(root, {}) }));
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), tooltip: am5.Tooltip.new(root, {}) }));

    // Summary shape: months/days array + income_by_month/expense_by_month (we named generically)
    // summary.labels -> array of labels (day or month), summary.income_arr & summary.expense_arr
    const labels = summary.labels || (summary.months || []);
    const incomeArr = summary.income_arr || (summary.income_by_month || []);
    const expenseArr = summary.expense_arr || (summary.expense_by_month || []);

    const data = labels.map((lab, i) => ({ label: lab, income: Number(incomeArr[i] || 0), expense: Number(expenseArr[i] || 0) }));
    xAxis.data.setAll(data);

    function createSeries(field, name, colorHex) {
      const series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name,
        xAxis,
        yAxis,
        valueYField: field,
        categoryXField: "label",
        clustered: true,
        tooltip: am5.Tooltip.new(root, { labelText: "{name}\n{categoryX}: {valueY}" })
      }));

      series.columns.template.setAll({ cornerRadiusTL: 6, cornerRadiusTR: 6, strokeOpacity: 0 });
      series.columns.template.set("fill", am5.color(colorHex));
      series.columns.template.set("stroke", am5.color(colorHex));
      series.data.setAll(data);
      series.bullets.push(() => am5.Bullet.new(root, { sprite: am5.Label.new(root, { text: "{valueY.formatNumber('#,###.##')}", centerY: am5.p50, centerX: am5.p50, populateText: true, fill: am5.color(0xffffff), fontSize: 11, opacity: 0.95 }) }));
      return series;
    }

    createSeries("income", "Income", 0x198754);
    createSeries("expense", "Expense", 0xdc3545);

    chart.children.push(am5.Legend.new(root, {}));
    chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "zoomX" }));
    chart.appear(1000, 100);

    return () => {
      try { if (chartRootRef.current) { chartRootRef.current.dispose(); chartRootRef.current = null; } } catch(e){/*ignore*/ }
    };
  }, [summary]);

  // --------------- CRUD functions (same as before) -----------------
  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.amount || isNaN(form.amount)) return alert("Enter valid amount");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('tx_type', form.tx_type);
      fd.append('amount', form.amount);
      fd.append('category', form.category);
      fd.append('spent_from', form.spent_from);
      fd.append('spent_by', form.spent_by);
      fd.append('description', form.description);
      fd.append('tx_date', form.tx_date ? form.tx_date.replace('T',' ') + ':00' : new Date().toISOString());
      fd.append('created_by', user?.id || 0);
      if (receiptFile) fd.append('receipt', receiptFile);
      const res = await addTransaction(fd);
      if (res.status === 'success') {
        setShowAdd(false); setReceiptFile(null);
        setForm({ ...form, amount:'', category:'', spent_by:'', description:'' });
        await loadSummary({ from: fromToParams.from, to: fromToParams.to });
        await loadTransactions(1, limit, { from: fromToParams.from, to: fromToParams.to });
      } else {
        alert(res.message || 'Failed');
      }
    } catch (err) { console.error(err); alert('Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    const res = await deleteTransaction(id);
    if (res.status === 'success') { await loadSummary({ from: fromToParams.from, to: fromToParams.to }); await loadTransactions(page, limit, { from: fromToParams.from, to: fromToParams.to }); }
    else alert(res.message || 'Failed to delete');
  };

  const formatINR = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  // UI render
  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col md={8}>
          <h4>Expense Tracker</h4>
          <p className="text-muted">Track company income and expenses. Select range to filter figures, chart and table.</p>
        </Col>
        <Col md={4} className="text-end">
          <Form.Select style={{ width: 220, display: 'inline-block', marginRight: 8 }} value={range} onChange={(e) => setRange(e.target.value)}>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
          </Form.Select>
          <Button onClick={()=>setShowAdd(true)}>+ Add Transaction</Button>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="p-3">
            <h6>Total Income</h6>
            <h3>₹ {loadingSummary ? <Spinner animation="border" size="sm" /> : (summary ? formatINR(summary.total_income) : '0.00')}</h3>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3">
            <h6>Total Expense</h6>
            <h3>₹ {loadingSummary ? <Spinner animation="border" size="sm" /> : (summary ? formatINR(summary.total_expense) : '0.00')}</h3>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card className="p-3">
            <h6>Income vs Expense ({range.replace('_',' ')})</h6>
            {loadingSummary ? <div className="text-center py-4"><Spinner/></div> : <div ref={chartDivRef} style={{ width: "100%", height: "420px" }}></div>}
          </Card>
        </Col>
      </Row>

      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Transactions</h6>
          <div className="text-muted small">Showing {transactions.length} of {total}</div>
        </div>

        {loadingTx ? <div className="text-center py-4"><Spinner/></div> : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th><th>Date</th><th>Type</th><th>Category</th><th>Amount (₹)</th><th>Spent From</th><th>Spent By</th><th>Description</th><th>Receipt</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t,i)=>(
                  <tr key={t.id}>
                    <td>{(page-1)*limit + i + 1}</td>
                    <td>{new Date(t.tx_date).toLocaleString()}</td>
                    <td>{t.tx_type}</td>
                    <td>{t.category}</td>
                    <td className={t.tx_type==='expense' ? 'text-danger' : 'text-success'}>₹ {formatINR(t.amount)}</td>
                    <td>{t.spent_from}</td>
                    <td>{t.spent_by}</td>
                    <td style={{maxWidth:250,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={t.description}>{t.description}</td>
                    <td>{t.receipt_filename ? <a href={`/api/uploads/receipts/${t.receipt_filename}`} target="_blank" rel="noreferrer">View</a> : '-'}</td>
                    <td><Button size="sm" variant="danger" onClick={()=>handleDelete(t.id)}>Delete</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">Page {page} </div>
              <div>
                <Button size="sm" variant="light" onClick={()=>loadTransactions(Math.max(1,page-1),limit,{from: fromToParams.from, to: fromToParams.to})} disabled={page===1}>Prev</Button>{' '}
                <Button size="sm" variant="light" onClick={()=>loadTransactions(page+1,limit,{from: fromToParams.from, to: fromToParams.to})} disabled={transactions.length < limit}>Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Add modal (unchanged) */}
      <Modal show={showAdd} onHide={()=>setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add Transaction</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            <Row>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Type</Form.Label><Form.Select value={form.tx_type} onChange={(e)=>setForm({...form,tx_type:e.target.value})}><option value="expense">Expense</option><option value="income">Income</option></Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Amount (₹)</Form.Label><Form.Control value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} /></Form.Group></Col>
            </Row>
            <Form.Group className="mb-2"><Form.Label>Category</Form.Label><Form.Control value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} placeholder="e.g., Salary, Fees, Stationery"/></Form.Group>
            <Row>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Spent From</Form.Label><Form.Select value={form.spent_from} onChange={(e)=>setForm({...form,spent_from:e.target.value})}><option value="company_account">Company Account</option><option value="personal">Personal</option></Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Spent By</Form.Label><Form.Control value={form.spent_by} onChange={(e)=>setForm({...form,spent_by:e.target.value})} placeholder="Name of person"/></Form.Group></Col>
            </Row>
            <Form.Group className="mb-2"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Date & Time</Form.Label><Form.Control type="datetime-local" value={form.tx_date} onChange={(e)=>setForm({...form,tx_date:e.target.value})} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Receipt (optional)</Form.Label><Form.Control type="file" accept="image/*,application/pdf" onChange={(e)=>setReceiptFile(e.target.files[0])} /></Form.Group>
            <div className="text-end mt-3">
              <Button variant="secondary" onClick={()=>setShowAdd(false)}>Cancel</Button>{' '}
              <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ExpenseTracker;
